/**
 * useDuplicates Hook
 *
 * A React hook for managing duplicate detection review via the API.
 * Provides methods for fetching duplicates, marking as reviewed, and force importing.
 */

import { useState, useCallback } from 'react';
import type {
  Duplicate,
  DuplicateFilter,
  DuplicatesListResponse,
  DuplicatesListMeta,
  MarkReviewedResponse,
  ForceImportResponse,
  BulkMarkReviewedResponse,
} from '@/types/accounting';

export interface UseDuplicatesReturn {
  /** Current list of duplicates */
  duplicates: Duplicate[];
  /** Metadata including counts */
  meta: DuplicatesListMeta | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch duplicates with optional filter */
  fetchDuplicates: (filter?: DuplicateFilter) => Promise<DuplicatesListResponse>;
  /** Mark a duplicate as reviewed (confirming it's a correct skip) */
  markReviewed: (id: number) => Promise<MarkReviewedResponse>;
  /** Force import a skipped duplicate as a new transaction */
  forceImport: (id: number) => Promise<ForceImportResponse>;
  /** Bulk mark multiple duplicates as reviewed */
  bulkMarkReviewed: (ids: number[]) => Promise<BulkMarkReviewedResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

/**
 * Hook for managing duplicate detection review
 *
 * @example
 * ```typescript
 * const { duplicates, meta, loading, error, fetchDuplicates, markReviewed, forceImport, bulkMarkReviewed } = useDuplicates();
 *
 * // Fetch unreviewed duplicates on mount
 * useEffect(() => {
 *   fetchDuplicates('unreviewed');
 * }, [fetchDuplicates]);
 *
 * // Mark a duplicate as reviewed (confirming the skip was correct)
 * const handleConfirmSkip = async (id: number) => {
 *   await markReviewed(id);
 * };
 *
 * // Force import a skipped duplicate as a new transaction
 * const handleForceImport = async (id: number) => {
 *   const result = await forceImport(id);
 *   console.log('Created transaction:', result.transaction.id);
 * };
 *
 * // Bulk mark all visible as reviewed
 * const handleMarkAllReviewed = async () => {
 *   const ids = duplicates.filter(d => !d.duplicateReviewed).map(d => d.id);
 *   await bulkMarkReviewed(ids);
 * };
 * ```
 */
export function useDuplicates(): UseDuplicatesReturn {
  const [duplicates, setDuplicates] = useState<Duplicate[]>([]);
  const [meta, setMeta] = useState<DuplicatesListMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch duplicates from the API
   */
  const fetchDuplicates = useCallback(
    async (filter: DuplicateFilter = 'unreviewed'): Promise<DuplicatesListResponse> => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/accounting/duplicates?filter=${filter}`, {
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Unauthorized');
          }
          throw new Error(`Failed to fetch duplicates: ${response.statusText}`);
        }

        const data = (await response.json()) as DuplicatesListResponse;
        setDuplicates(data.duplicates);
        setMeta(data.meta);
        return data;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to load duplicates';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Mark a duplicate as reviewed (confirming the skip was correct)
   */
  const markReviewed = useCallback(async (id: number): Promise<MarkReviewedResponse> => {
    setLoading(true);
    setError(null);

    // Optimistic update
    setDuplicates((prev) =>
      prev.map((d) => (d.id === id ? { ...d, duplicateReviewed: true } : d))
    );

    try {
      const response = await fetch(`/accounting/duplicates/${id}/mark_reviewed`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!response.ok) {
        // Revert optimistic update on failure
        setDuplicates((prev) =>
          prev.map((d) => (d.id === id ? { ...d, duplicateReviewed: false } : d))
        );

        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Duplicate not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to mark reviewed: ${response.statusText}`);
      }

      const data = (await response.json()) as MarkReviewedResponse;

      // Update meta counts
      if (meta) {
        setMeta({
          ...meta,
          unreviewedCount: Math.max(0, meta.unreviewedCount - 1),
        });
      }

      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to mark duplicate as reviewed';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [meta]);

  /**
   * Force import a skipped duplicate as a new transaction
   */
  const forceImport = useCallback(async (id: number): Promise<ForceImportResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/accounting/duplicates/${id}/force_import`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Duplicate not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to force import: ${response.statusText}`);
      }

      const data = (await response.json()) as ForceImportResponse;

      // Update the duplicate in local state (status changes to 'processed')
      setDuplicates((prev) =>
        prev.map((d) =>
          d.id === id
            ? { ...d, status: data.duplicate.status, duplicateReviewed: data.duplicate.duplicateReviewed }
            : d
        )
      );

      // Update meta counts
      if (meta) {
        setMeta({
          ...meta,
          unreviewedCount: Math.max(0, meta.unreviewedCount - 1),
        });
      }

      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to force import duplicate';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [meta]);

  /**
   * Bulk mark multiple duplicates as reviewed
   */
  const bulkMarkReviewed = useCallback(
    async (ids: number[]): Promise<BulkMarkReviewedResponse> => {
      if (ids.length === 0) {
        return { success: true, updatedCount: 0 };
      }

      setLoading(true);
      setError(null);

      // Optimistic update
      const previousDuplicates = duplicates;
      setDuplicates((prev) =>
        prev.map((d) => (ids.includes(d.id) ? { ...d, duplicateReviewed: true } : d))
      );

      try {
        const response = await fetch('/accounting/duplicates/bulk_mark_reviewed', {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify({ ids }),
        });

        if (!response.ok) {
          // Revert optimistic update on failure
          setDuplicates(previousDuplicates);

          if (response.status === 401) {
            throw new Error('Unauthorized');
          }
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to bulk mark reviewed: ${response.statusText}`);
        }

        const data = (await response.json()) as BulkMarkReviewedResponse;

        // Update meta counts
        if (meta) {
          setMeta({
            ...meta,
            unreviewedCount: Math.max(0, meta.unreviewedCount - data.updatedCount),
          });
        }

        return data;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to bulk mark duplicates as reviewed';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [duplicates, meta]
  );

  return {
    duplicates,
    meta,
    loading,
    error,
    fetchDuplicates,
    markReviewed,
    forceImport,
    bulkMarkReviewed,
    clearError,
  };
}
